import type { Expression, FunctionName } from '@aibi/contracts';
import { assertNever } from '@aibi/shared';

export interface FunctionCall {
  readonly name: FunctionName;
  readonly argCount: number;
}

export interface ExpressionReferences {
  readonly columnIds: readonly string[];
  readonly measureIds: readonly string[];
  readonly functionCalls: readonly FunctionCall[];
  readonly containsAggregate: boolean;
}

/**
 * Recorre el AST y recopila las referencias a columnas, medidas y funciones.
 * Base para validar integridad referencial y detectar dependencias entre medidas.
 *
 * @example
 * collectExpressionReferences({ type: 'measure', measureId: 'total_sales' }).measureIds
 * // ['total_sales']
 */
export function collectExpressionReferences(expression: Expression): ExpressionReferences {
  const columnIds = new Set<string>();
  const measureIds = new Set<string>();
  const functionCalls: FunctionCall[] = [];
  let containsAggregate = false;

  const visit = (node: Expression): void => {
    switch (node.type) {
      case 'literal':
        return;
      case 'column':
        columnIds.add(node.columnId);
        return;
      case 'measure':
        measureIds.add(node.measureId);
        return;
      case 'unary':
        visit(node.operand);
        return;
      case 'binary':
        visit(node.left);
        visit(node.right);
        return;
      case 'function':
        functionCalls.push({ name: node.name, argCount: node.args.length });
        node.args.forEach(visit);
        return;
      case 'aggregate':
        containsAggregate = true;
        if (node.argument) visit(node.argument);
        if (node.filter) visit(node.filter);
        return;
      case 'case':
        for (const branch of node.branches) {
          visit(branch.when);
          visit(branch.then);
        }
        if (node.else) visit(node.else);
        return;
      default:
        assertNever(node, 'Tipo de expresion no soportado');
    }
  };

  visit(expression);
  return {
    columnIds: [...columnIds],
    measureIds: [...measureIds],
    functionCalls,
    containsAggregate,
  };
}

/**
 * Copia el AST sustituyendo cada id de columna. Sirve para traducir expresiones que
 * usan ids de otro espacio (por ejemplo, los de Discovery) a ids del modelo.
 *
 * @example
 * mapExpressionColumns(expr, (id) => ids.get(id) ?? id);
 */
export function mapExpressionColumns(
  expression: Expression,
  mapColumn: (columnId: string) => string,
): Expression {
  const map = (node: Expression): Expression => {
    switch (node.type) {
      case 'literal':
      case 'measure':
        return { ...node };
      case 'column':
        return { type: 'column', columnId: mapColumn(node.columnId) };
      case 'unary':
        return { ...node, operand: map(node.operand) };
      case 'binary':
        return { ...node, left: map(node.left), right: map(node.right) };
      case 'function':
        return { ...node, args: node.args.map(map) };
      case 'aggregate':
        return {
          type: 'aggregate',
          function: node.function,
          ...(node.argument ? { argument: map(node.argument) } : {}),
          ...(node.filter ? { filter: map(node.filter) } : {}),
        };
      case 'case':
        return {
          type: 'case',
          branches: node.branches.map((branch) => ({
            when: map(branch.when),
            then: map(branch.then),
          })),
          ...(node.else ? { else: map(node.else) } : {}),
        };
      default:
        return assertNever(node, 'Tipo de expresion no soportado');
    }
  };
  return map(expression);
}
